import './WinnerSelection2.scss';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ActionIcon, SegmentedControl } from '@mantine/core';
import { plural } from '@srm/ui/lib/pluralize';
import { MdChevronLeft, MdChevronRight, MdFullscreen, MdFullscreenExit, MdSearch } from 'react-icons/md';
import Button from '@srm/ui/button';
import Hint from '@srm/ui/hint';
import Input from '@srm/ui/input';
import { useFullscreen } from '@srm/ui/hooks/useFullscreen';
import { sendNotification } from '@srm/ui/lib/notifications';
import { WinnerSelection2Service } from '@services/WinnerSelection2Service';
import { RouteNames } from '@/router/router.types';
import { IOffer, IOfferAction, IOfferCrit, ITableDemandItem } from '@models/winnerSelection/ITableDemand';
import { ICustomComponentProps } from '../../ICustomComponentProps';
import OfferCustomForm from '../WinnerSelection/OfferCustomForm';
import OfferAttachmentsArchive, { downloadOfferAttachments } from '../WinnerSelection/OfferAttachmentsArchive';
import { useWinnerMatrix, ICommandOutcome } from './hooks/useWinnerMatrix';
import { useParticipantWindow } from './hooks/useParticipantWindow';
import {
    EditableResult,
    IMatrixCell,
    IMatrixCellCommand,
    IMatrixLot,
    IMatrixParticipant,
} from './WinnerSelection2.types';
import {
    buildIndex,
    cellKeyOf,
    decidedPositions,
    filterLots,
    formatMatrixMessage,
    otherComplianceRevisions,
    otherCriterionRevisions,
    priceModeLabel,
    UNIT_PRICE_METRIC,
    UNIT_PRICE_TITLE,
} from './utils/matrixView';
import MatrixGrid from './parts/MatrixGrid';
import MatrixState from './parts/MatrixState';
import MatrixSkeleton from './parts/MatrixSkeleton';
import CriterionEditor from './parts/CriterionEditor';
import { CellIntent } from './parts/OfferCell';
import OfferDetailsDialog, { OfferActionIntent } from './parts/OfferDetailsDialog';
import ChooseOfferDialog from './parts/ChooseOfferDialog';
import ComplianceDialog from './parts/ComplianceDialog';
import LotDecisionDialog from './parts/LotDecisionDialog';

import type { PositionCriterionHandler } from './WinnerSelection2.types';

type Dialog =
    | { kind: 'details' | 'choose' | 'compliance'; positionKey: string; participantKey: string }
    | { kind: 'positionCriterion'; positionKey: string; participantKey: string; critId: string; opened: number }
    | { kind: 'lot'; lotKey: string; participantKey: string }
    | { kind: 'customForm'; offerKey: string; action: IOfferAction }
    | { kind: 'attachmentsArchive'; offerKey: string; action: IOfferAction }
    | {
          kind: 'criterion';
          item: ITableDemandItem;
          participantKey: string;
          value: IOfferCrit;
          /** Повторный клик по той же ячейке открывает форму заново. */
          opened: number;
      };

const complianceSavedMessage = (others: Record<string, string> | undefined) => {
    const count = Object.keys(others ?? {}).length + 1;
    return others
        ? `Ответ по ТЗ сохранён в ${count} ${plural(count, ['предложении', 'предложениях', 'предложениях'])} поставщика`
        : 'Ответ по ТЗ сохранён';
};

const WinnerSelection2Content = <TFieldValues extends object = object>({
    component,
    docId,
}: ICustomComponentProps<TFieldValues>) => {
    const tableKey = component.params?.tableKey;
    const { matrix, state, error, busy: saving, reload, run } = useWinnerMatrix(docId, tableKey);
    const busy = saving || state !== 'ready';
    const { fullscreen: isFullscreen, toggle: toggleFullscreen } = useFullscreen('document', {
        fallback: true,
        exitOnUnmount: true,
    });

    const [query, setQuery] = useState('');
    const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
    const [dialog, setDialog] = useState<Dialog | null>(null);
    const [dialogError, setDialogError] = useState<string>();
    // Показатель общий для всей матрицы: страницы участников, скрытые поиском и свёрнутые лоты.
    const [metricKey, setMetricKey] = useState(UNIT_PRICE_METRIC);
    const metricLabelId = useId();
    const returnFocus = useRef<HTMLElement | null>(null);
    const viewportRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const participants = matrix?.participants ?? [];
    const pager = useParticipantWindow(rootRef, participants.length);
    const visibleParticipants = participants.slice(pager.offset, pager.offset + pager.size);

    const index = useMemo(() => (matrix ? buildIndex(matrix) : undefined), [matrix]);
    const lots = useMemo(() => (matrix && index ? filterLots(matrix, index, query) : []), [matrix, index, query]);

    const metrics = useMemo(() => matrix?.cellMetrics ?? [], [matrix?.cellMetrics]);
    const metricOptions = useMemo(
        () => [
            { value: UNIT_PRICE_METRIC, label: UNIT_PRICE_TITLE },
            ...metrics.map((item) => ({ value: item.key, label: item.title })),
        ],
        [metrics],
    );
    // Показатель, пропавший из ответа сервера (например, скрытый), возвращает цену за единицу.
    const current = Math.max(
        0,
        metricOptions.findIndex((option) => option.value === metricKey),
    );
    const metric = current > 0 ? metrics[current - 1] : null;
    const nextMetric = metrics.length > 0 ? metricOptions[(current + 1) % metricOptions.length] : null;
    const nextMetricValue = nextMetric?.value;
    const onNextMetric = useCallback(() => {
        if (nextMetricValue) setMetricKey(nextMetricValue);
    }, [nextMetricValue]);

    useEffect(() => {
        setDialog(null);
        setCollapsed(new Set());
        setMetricKey(UNIT_PRICE_METRIC);
    }, [docId, tableKey]);

    const openDialog = useCallback((next: Dialog, trigger: HTMLElement | null) => {
        returnFocus.current = trigger;
        setDialogError(undefined);
        setDialog(next);
    }, []);

    const closeDialog = useCallback(() => {
        setDialog(null);
        setDialogError(undefined);
        const target = returnFocus.current;
        returnFocus.current = null;
        requestAnimationFrame(() => {
            if (target?.isConnected) target.focus();
            else viewportRef.current?.focus();
        });
    }, []);

    const finish = useCallback((outcome: ICommandOutcome, success: string) => {
        if (outcome.obsolete) return false;
        if (!outcome.ok) {
            setDialogError(outcome.message);
            return false;
        }
        sendNotification({ message: success, variant: 'green' });
        outcome.warnings.forEach((warning) => sendNotification({ message: warning, variant: 'yellow' }));
        return true;
    }, []);

    const saveCell = useCallback(
        async (
            cell: IMatrixCell,
            change: Pick<IMatrixCellCommand, 'decision' | 'compliance'>,
            success: string,
            closeOnSuccess = true,
        ) => {
            if (!matrix || !tableKey) return;
            const outcome = await run(() =>
                WinnerSelection2Service.saveCell(matrix.requestKey, tableKey, {
                    offerKey: cell.offerKey,
                    positionKey: cell.positionKey,
                    expectedRevision: cell.revision,
                    ...change,
                }),
            );
            const saved = finish(outcome, success);
            if (saved && closeOnSuccess) closeDialog();
            // Без открытого диалога (сброс из ячейки) отказ показываем уведомлением.
            if (!saved && !closeOnSuccess && !outcome.obsolete && outcome.message)
                sendNotification({ message: outcome.message, variant: 'red' });
        },
        [matrix, tableKey, run, finish, closeDialog],
    );

    const onCellIntent = useCallback(
        (intent: CellIntent, cell: IMatrixCell, trigger: HTMLElement) => {
            if (intent === 'reset') {
                void saveCell(
                    cell,
                    { decision: { result: 'none', selectedQuantity: null } },
                    'Выбор и количество сброшены. Ответ ТЗ и комментарий сохранены',
                    false,
                );
                return;
            }
            openDialog({ kind: intent, positionKey: cell.positionKey, participantKey: cell.participantKey }, trigger);
        },
        [openDialog, saveCell],
    );

    const onDecideLot = useCallback(
        (lot: IMatrixLot, participant: IMatrixParticipant, trigger: HTMLElement) =>
            openDialog({ kind: 'lot', lotKey: lot.key, participantKey: participant.key }, trigger),
        [openDialog],
    );

    const onCriterion = useCallback(
        (item: ITableDemandItem, participant: IMatrixParticipant, value: IOfferCrit, trigger: HTMLElement) =>
            openDialog(
                { kind: 'criterion', item, participantKey: participant.key, value, opened: Date.now() },
                trigger,
            ),
        [openDialog],
    );

    const onPositionCriterion = useCallback<PositionCriterionHandler>(
        (cell, criterion, trigger) =>
            openDialog(
                {
                    kind: 'positionCriterion',
                    positionKey: cell.positionKey,
                    participantKey: cell.participantKey,
                    critId: criterion.item.critId,
                    opened: Date.now(),
                },
                trigger,
            ),
        [openDialog],
    );

    const toggleLot = useCallback((lotKey: string) => {
        setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(lotKey)) next.delete(lotKey);
            else next.add(lotKey);
            return next;
        });
    }, []);

    const onOfferAction = useCallback(
        (intent: OfferActionIntent, action: IOfferAction, offerKey: string) => {
            if (!tableKey) return;
            if (intent === 'open') {
                window.open(`.${RouteNames.DOCUMENT.replace(':id', offerKey)}`, '_blank');
            } else if (intent === 'download') {
                downloadOfferAttachments(offerKey, tableKey, action, () =>
                    openDialog({ kind: 'attachmentsArchive', offerKey, action }, returnFocus.current),
                );
            } else {
                // Фокус после формы возвращается туда же, куда и после деталей предложения.
                openDialog({ kind: 'customForm', offerKey, action }, returnFocus.current);
            }
        },
        [tableKey, openDialog],
    );

    if (state === 'loading' && !matrix) {
        return (
            <div className="winner-selection2">
                <div className="winner-selection2__toolbar">
                    <h2 className="winner-selection2__title">Выбор победителя</h2>
                </div>
                <MatrixSkeleton />
            </div>
        );
    }

    if (!matrix || !index) {
        return (
            <div className="winner-selection2">
                <div className="winner-selection2__toolbar">
                    <h2 className="winner-selection2__title">Выбор победителя</h2>
                </div>
                <div className="ws2-viewport ws2-viewport--state">
                    <MatrixState kind="error" error={error} onAction={reload} />
                </div>
            </div>
        );
    }

    const renderDialog = () => {
        if (!dialog || !tableKey) return null;

        if (dialog.kind === 'attachmentsArchive') {
            return (
                <OfferAttachmentsArchive
                    tableKey={tableKey}
                    offerKey={dialog.offerKey}
                    action={dialog.action}
                    onClose={closeDialog}
                />
            );
        }

        if (dialog.kind === 'customForm') {
            return (
                <OfferCustomForm
                    tableKey={tableKey}
                    value={{ key: dialog.offerKey } as IOffer}
                    action={dialog.action}
                    onClose={closeDialog}
                />
            );
        }

        const participant = participants.find((item) => item.key === dialog.participantKey);
        if (!participant) return null;

        if (dialog.kind === 'positionCriterion') {
            const cell = index.cells.get(cellKeyOf(dialog.positionKey, dialog.participantKey));
            const criterion = cell?.criteria?.find((entry) => entry.item.critId === dialog.critId);
            const settings = matrix.positionCriteriaSettings;
            if (!cell || !criterion) return null;
            const otherOffers = otherCriterionRevisions(matrix, cell, criterion.item.critId);
            return (
                <CriterionEditor
                    key={`${cell.offerKey}:${dialog.positionKey}:${dialog.critId}:${dialog.opened}`}
                    tableKey={tableKey}
                    requestKey={matrix.requestKey}
                    participantKey={cell.offerKey}
                    item={criterion.item}
                    value={criterion.value}
                    showComment={criterion.showComment}
                    contextHint={cell.lotOffer ? 'Ответ общий для всех позиций этого предложения' : undefined}
                    disabledHint={settings?.offerCritDisabledHint}
                    ratingScaleHints={settings?.ratingScaleHints}
                    customScaleCrits={settings?.customScaleCrits}
                    editButtonName={settings?.editOfferCritButtonName}
                    offerAnswerSettings={settings?.offerAnswerSettings}
                    showAnswerInScore={settings?.showAnswerInScore}
                    otherSupplierOffers={criterion.value.allowEdit ? Object.keys(otherOffers).length : 0}
                    onSaved={closeDialog}
                    saveAction={async (form) => {
                        const outcome = await run(() =>
                            WinnerSelection2Service.savePositionCriterion(matrix.requestKey, tableKey, {
                                otherOfferRevisions: form.offerCrit.applyToAllSupplierOffers ? otherOffers : undefined,
                                offerKey: cell.offerKey,
                                positionKey: cell.positionKey,
                                participantKey: cell.participantKey,
                                critId: criterion.item.critId,
                                expectedRevision: criterion.revision,
                                answer: form.offerCrit.answer ?? null,
                                description: form.offerCrit.description ?? null,
                                score: form.offerCrit.score ?? null,
                            }),
                        );
                        if (!outcome.ok) throw new Error(outcome.message ?? 'Не удалось сохранить ответ');
                    }}
                />
            );
        }

        if (dialog.kind === 'criterion') {
            const criteriaTableKey = matrix.criteria.tableKey;
            const settings = matrix.criteria.settings;
            const item = matrix.criteria.items.find((item) => item.critId === dialog.item.critId);
            const value = item?.offers[dialog.participantKey];
            if (!criteriaTableKey || !item || !value) return null;
            return (
                <CriterionEditor
                    key={`${dialog.item.critId}:${dialog.participantKey}:${dialog.opened}`}
                    tableKey={criteriaTableKey}
                    requestKey={matrix.requestKey}
                    participantKey={dialog.participantKey}
                    item={item}
                    value={value}
                    disabledHint={settings?.offerCritDisabledHint}
                    ratingScaleHints={settings?.ratingScaleHints}
                    customScaleCrits={settings?.customScaleCrits}
                    editButtonName={settings?.editOfferCritButtonName}
                    offerAnswerSettings={settings?.offerAnswerSettings}
                    showAnswerInScore={settings?.showAnswerInScore}
                    onSaved={closeDialog}
                    saveAction={async (form) => {
                        const outcome = await run(() =>
                            WinnerSelection2Service.saveCriterion(matrix.requestKey, tableKey, {
                                participantKey: dialog.participantKey,
                                critId: item.critId,
                                expectedRevision:
                                    matrix.criteria.revisions?.[item.critId]?.[dialog.participantKey] ?? '',
                                answer: form.offerCrit.answer ?? null,
                                description: form.offerCrit.description ?? null,
                                score: form.offerCrit.score ?? null,
                            }),
                        );
                        if (!outcome.ok)
                            throw new Error(outcome.message ?? 'Сохранение больше не относится к открытой закупке');
                    }}
                />
            );
        }

        if (dialog.kind === 'lot') {
            const lot = index.lots.get(dialog.lotKey);
            if (!lot) return null;
            const summary = lot.participants.find((item) => item.participantKey === participant.key);
            const shown = lots.find((item) => item.lot.key === lot.key);
            const hidden = collapsed.has(lot.key)
                ? lot.positionKeys.length
                : lot.positionKeys.length - (shown?.positions.length ?? 0);
            const decideLot = async (result: EditableResult) => {
                const outcome = await run(() =>
                    WinnerSelection2Service.saveLot(matrix.requestKey, tableKey, {
                        lotKey: lot.key,
                        participantKey: participant.key,
                        result,
                        expectedRevisions: summary?.offerRevisions ?? {},
                    }),
                );
                const message = result === 'none' ? 'Выбор по лоту сброшен' : 'Выбор применён ко всем позициям лота';
                if (finish(outcome, message)) closeDialog();
            };
            return (
                <LotDecisionDialog
                    lot={lot}
                    participant={participant}
                    hiddenPositions={hidden}
                    busy={saving}
                    blocked={state !== 'ready'}
                    error={dialogError}
                    onClose={closeDialog}
                    onDecide={(result) => void decideLot(result)}
                />
            );
        }

        const cell = index.cells.get(cellKeyOf(dialog.positionKey, dialog.participantKey));
        const position = index.positions.get(dialog.positionKey);
        if (!cell || !position) return null;

        if (dialog.kind === 'choose') {
            return (
                <ChooseOfferDialog
                    matrix={matrix}
                    cell={cell}
                    position={position}
                    participant={participant}
                    busy={saving}
                    blocked={state !== 'ready'}
                    error={dialogError}
                    onClose={closeDialog}
                    onChoose={(result, quantity) =>
                        void saveCell(
                            cell,
                            { decision: { result, selectedQuantity: quantity } },
                            `Выбрано: ${result === 'main' ? 'основной' : 'резервный'}` +
                                (cell.quantityFixed ? '' : '. Количество можно изменить в деталях'),
                        )
                    }
                />
            );
        }

        if (dialog.kind === 'compliance') {
            return (
                <ComplianceDialog
                    matrix={matrix}
                    cell={cell}
                    position={position}
                    participant={participant}
                    busy={saving}
                    blocked={state !== 'ready'}
                    error={dialogError}
                    onClose={closeDialog}
                    otherOffers={otherComplianceRevisions(matrix, cell)}
                    onSave={(compliance) =>
                        void saveCell(cell, { compliance }, complianceSavedMessage(compliance.otherOfferRevisions))
                    }
                />
            );
        }

        return (
            // Без key по ревизии: после ответа 409 диалог получает свежую ячейку
            // (новую ревизию для повтора), а введённый черновик сохраняется.
            <OfferDetailsDialog
                key={cell.offerKey}
                matrix={matrix}
                cell={cell}
                position={position}
                participant={participant}
                busy={saving}
                blocked={state !== 'ready'}
                error={dialogError}
                onClose={closeDialog}
                otherOffers={otherComplianceRevisions(matrix, cell)}
                onSave={(change) =>
                    void saveCell(
                        cell,
                        change,
                        change.compliance?.otherOfferRevisions
                            ? complianceSavedMessage(change.compliance.otherOfferRevisions)
                            : 'Изменения сохранены',
                    )
                }
                onAction={(intent, action) => onOfferAction(intent, action, cell.offerKey)}
            />
        );
    };

    const totalPositions = matrix.positions.length;
    const firstVisible = participants.length === 0 ? 0 : pager.offset + 1;
    const lastVisible = pager.offset + visibleParticipants.length;

    return (
        <div ref={rootRef} className={clsx('winner-selection2', isFullscreen && 'winner-selection2--fullscreen')}>
            <div className="winner-selection2__toolbar">
                <div className="winner-selection2__heading">
                    <h2 className="winner-selection2__title">Выбор победителя</h2>
                    <span className="winner-selection2__meta">
                        {matrix.lots.length} {plural(matrix.lots.length, ['лот', 'лота', 'лотов'])} · {totalPositions}{' '}
                        {plural(totalPositions, ['позиция', 'позиции', 'позиций'])} · {participants.length}{' '}
                        {plural(participants.length, ['заявка', 'заявки', 'заявок'])}
                    </span>
                </div>
                <div className="winner-selection2__actions">
                    <span
                        className={clsx(
                            'winner-selection2__price-mode',
                            matrix.priceMode === 'unknown' && 'winner-selection2__price-mode--unknown',
                        )}
                    >
                        {priceModeLabel(matrix.priceMode)}
                    </span>
                    <Button
                        variant="outline-neutral"
                        size="sm"
                        startAdornment={
                            isFullscreen ? (
                                <MdFullscreenExit size="18px" aria-hidden />
                            ) : (
                                <MdFullscreen size="18px" aria-hidden />
                            )
                        }
                        onClick={() => void toggleFullscreen()}
                    >
                        {isFullscreen ? 'Выйти из полного экрана' : 'На весь экран'}
                    </Button>
                </div>
            </div>

            {participants.length > 0 && totalPositions > 0 && (
                <div className="winner-selection2__controls">
                    <div className="winner-selection2__search">
                        <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onClear={() => setQuery('')}
                            placeholder="Найти лот или позицию"
                            aria-label="Найти лот или позицию"
                            leftSection={<MdSearch size="18px" aria-hidden />}
                        />
                    </div>
                    {metrics.length > 0 && (
                        <div className="winner-selection2__metric">
                            <span id={metricLabelId}>Показатель в ячейках</span>
                            <SegmentedControl
                                className="winner-selection2__metric-segments"
                                aria-labelledby={metricLabelId}
                                size="xs"
                                radius="sm"
                                data={metricOptions}
                                value={metric?.key ?? UNIT_PRICE_METRIC}
                                onChange={setMetricKey}
                            />
                        </div>
                    )}
                    {participants.length > pager.size && (
                        <nav className="ws2-pager" aria-label="Участники">
                            <ActionIcon
                                variant="default"
                                size="lg"
                                aria-label="Предыдущие участники"
                                disabled={!pager.canPrev}
                                onClick={pager.prev}
                            >
                                <MdChevronLeft size="20px" aria-hidden />
                            </ActionIcon>
                            <span aria-live="polite">
                                {firstVisible}–{lastVisible} из {participants.length} участников
                            </span>
                            <ActionIcon
                                variant="default"
                                size="lg"
                                aria-label="Следующие участники"
                                disabled={!pager.canNext}
                                onClick={pager.next}
                            >
                                <MdChevronRight size="20px" aria-hidden />
                            </ActionIcon>
                        </nav>
                    )}
                </div>
            )}

            {state === 'loading' && participants.length > 0 && totalPositions > 0 && (
                <div className="ws2-loading-caption" role="status">
                    Обновляем предложения…
                </div>
            )}

            {(matrix.readonlyReason || matrix.diagnostics.length > 0 || state === 'error') && (
                <div className="winner-selection2__notice">
                    {state === 'error' && error && participants.length > 0 && totalPositions > 0 && (
                        <MatrixState
                            kind="error"
                            compact
                            description="Показаны ранее загруженные данные. Обновите их, чтобы продолжить выбор победителя"
                            error={error}
                            onAction={reload}
                        />
                    )}
                    {matrix.readonlyReason && (
                        <Hint title={formatMatrixMessage(matrix.readonlyReason)} tone="info" maxWidth="100%" />
                    )}
                    {matrix.diagnostics.length > 0 && (
                        <Hint title="Часть данных не сопоставлена и не показана" tone="warning" maxWidth="100%">
                            <ul className="winner-selection2__diagnostics">
                                {matrix.diagnostics.map((text) => (
                                    <li key={text}>{text}</li>
                                ))}
                            </ul>
                        </Hint>
                    )}
                </div>
            )}

            {participants.length === 0 || totalPositions === 0 ? (
                state === 'loading' ? (
                    <MatrixSkeleton />
                ) : (
                    <div className="ws2-viewport ws2-viewport--state">
                        <MatrixState
                            kind={state === 'error' ? 'error' : totalPositions === 0 ? 'positions' : 'empty'}
                            error={state === 'error' ? error : undefined}
                            description={
                                state !== 'error' && totalPositions > 0 ? (matrix.emptyText ?? undefined) : undefined
                            }
                            onAction={reload}
                        />
                    </div>
                )
            ) : (
                <MatrixGrid
                    ref={viewportRef}
                    matrix={matrix}
                    index={index}
                    lots={lots}
                    participants={visibleParticipants}
                    collapsed={collapsed}
                    busy={busy}
                    metric={metric}
                    nextMetricTitle={nextMetric?.label ?? null}
                    onNextMetric={onNextMetric}
                    onClearSearch={() => setQuery('')}
                    onToggleLot={toggleLot}
                    onCellIntent={onCellIntent}
                    onDecideLot={onDecideLot}
                    onCriterion={onCriterion}
                    onPositionCriterion={onPositionCriterion}
                />
            )}

            {participants.length > 0 && totalPositions > 0 && (
                <div className="winner-selection2__footer">
                    <span>Ранг помогает сравнить цены. Основной и резервный объём учитываются отдельно</span>
                    <span>
                        Основной поставщик выбран: {decidedPositions(matrix)} из {totalPositions} поз.
                    </span>
                </div>
            )}

            {renderDialog()}
        </div>
    );
};

// Смена документа изолирует запросы, черновики и команды предыдущей закупки.
const WinnerSelection2 = <TFieldValues extends object = object>(props: ICustomComponentProps<TFieldValues>) => (
    <WinnerSelection2Content key={`${props.docId}:${props.component.params?.tableKey ?? ''}`} {...props} />
);

export default WinnerSelection2;
